<template>
  <div class="flex flex-col gap-2 text-left" data-testid="coverage-responsible-field">
    <v-autocomplete
      :model-value="responsibleId ?? undefined"
      :items="options"
      item-title="name"
      item-value="id"
      :label="`${COVERAGE_RESPONSIBLE_LABEL} *`"
      :loading="isLoading"
      :disabled="disabled || !isSupported || !healthUnitId"
      :error-messages="error ? [error] : []"
      :search="search"
      :no-data-text="isLoading ? 'Carregando...' : 'Nenhum responsável encontrado'"
      variant="outlined"
      color="primary20"
      density="comfortable"
      clearable
      data-testid="coverage-responsible-select"
      @update:model-value="onSelect"
      @update:search="search = $event ?? ''"
    />

    <p class="text-xs text-[#68688D]" data-testid="coverage-responsible-hint">{{ hintText }}</p>

    <button
      v-if="canOfferCreate"
      type="button"
      class="self-start text-sm font-bold text-primary30 hover:underline disabled:opacity-50"
      :disabled="isCreating"
      data-testid="coverage-responsible-create-button"
      @click="createFromSearch"
    >
      Criar "{{ trimmedSearch }}" na lista {{ unitSuffix }}
    </button>

    <p v-if="loadError" class="text-xs text-negative" data-testid="coverage-responsible-load-error">
      {{ loadError }}
      <button
        type="button"
        class="underline font-semibold"
        data-testid="coverage-responsible-retry"
        @click="reload"
      >
        Tentar novamente
      </button>
    </p>

    <v-textarea
      :model-value="note"
      :label="COVERAGE_NOTE_LABEL"
      :counter="COVERAGE_NOTE_MAX_LENGTH"
      :maxlength="COVERAGE_NOTE_MAX_LENGTH"
      rows="2"
      auto-grow
      variant="outlined"
      color="primary20"
      density="comfortable"
      :disabled="disabled"
      data-testid="coverage-note-input"
      @update:model-value="emit('update:note', $event ?? '')"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { useToast } from 'vue-toastification'
import { useCoverageResponsibles } from '@/hooks/useCoverageResponsibles'
import { usePermissionsStore } from '@/pinia-store/usePermissionsStore'
import { AuthorizationPermissions, AuthorizationResourcesAndSubjects } from '@/types/authorization'
import { useErrorToast } from '@/composables/useErrorToast'
import { getCoverageErrorMessage } from '@/utils/coverageErrors'
import {
  COVERAGE_NOTE_LABEL,
  COVERAGE_NOTE_MAX_LENGTH,
  COVERAGE_RESPONSIBLE_LABEL
} from '@/utils/coverage'

const props = withDefaults(
  defineProps<{
    healthUnitId: number | null
    responsibleId: number | null
    note: string
    healthUnitName?: string | null
    fallbackName?: string | null
    error?: string | null
    disabled?: boolean
  }>(),
  { healthUnitName: null, fallbackName: null, error: null, disabled: false }
)

const emit = defineEmits<{
  'update:responsibleId': [value: number | null]
  'update:note': [value: string]
  'update:responsibleName': [value: string | null]
}>()

const toast = useToast()
const { notifyError, notifyErrorMessage } = useErrorToast()
const permissionsStore = usePermissionsStore()
const { selectableItems, isLoading, loadError, isSupported, reload, create } =
  useCoverageResponsibles(() => props.healthUnitId)

const search = ref('')
const isCreating = ref(false)
const trimmedSearch = computed(() => search.value.trim())

const options = computed(() => {
  const list = selectableItems.value.map(({ id, name }) => ({ id, name }))
  const missingCurrent =
    props.responsibleId != null && !list.some((option) => option.id === props.responsibleId)
  if (missingCurrent && props.fallbackName) {
    list.push({ id: props.responsibleId as number, name: `${props.fallbackName} (inativo)` })
  }
  return list
})

// M9-C / Q39: criar inline só para quem tem a permissão de editar a unidade.
const canCreate = computed(() => {
  if (!props.healthUnitId) return false
  return permissionsStore.check({
    permission: AuthorizationPermissions.HealthUnitEditing,
    resource: {
      objectType: AuthorizationResourcesAndSubjects.HealthUnit,
      objectId: props.healthUnitId
    }
  })
})

const canOfferCreate = computed(() => {
  if (!canCreate.value || !isSupported.value || trimmedSearch.value.length < 2) return false
  const needle = trimmedSearch.value.toLocaleLowerCase('pt-BR')
  return !selectableItems.value.some((item) => item.name.toLocaleLowerCase('pt-BR') === needle)
})

const unitSuffix = computed(() =>
  props.healthUnitName ? `da ${props.healthUnitName}` : 'da unidade'
)

const hintText = computed(() => {
  if (!isSupported.value) return 'A lista de responsáveis ainda não está disponível para esta unidade.'
  if (!isLoading.value && !loadError.value && selectableItems.value.length === 0 && !canCreate.value)
    return 'Nenhum responsável ativo nesta unidade. Peça a quem edita a unidade para cadastrar um item.'
  return props.healthUnitName
    ? `Obrigatório. Lista da unidade ${props.healthUnitName}.`
    : 'Obrigatório. Lista da unidade.'
})

function onSelect(value: number | string | null | undefined) {
  const id = value == null || value === '' ? null : Number(value)
  emit('update:responsibleId', id)
  emit('update:responsibleName', options.value.find((option) => option.id === id)?.name ?? null)
}

async function createFromSearch() {
  isCreating.value = true
  try {
    const created = await create(trimmedSearch.value)
    emit('update:responsibleId', created.id)
    emit('update:responsibleName', created.name)
    search.value = ''
    toast.success('Responsável criado na lista da unidade.')
  } catch (error) {
    const coverageMessage = getCoverageErrorMessage(error)
    if (coverageMessage) notifyErrorMessage(coverageMessage)
    else notifyError(error, 'Não foi possível criar o responsável pela cobertura')
  } finally {
    isCreating.value = false
  }
}
</script>
